import React, { useState, useEffect } from 'react';
import { 
  FiEdit2, 
  FiSquare, 
  FiTrash2, 
  FiArrowRight, 
  FiType, 
  FiCircle, 
  FiX,
  FiMenu,
  FiEye,
  FiCode,
  FiSettings,
  FiInfo
} from 'react-icons/fi';
import './ElementEditor.css';

const ElementEditor = ({ 
  visible, 
  element, 
  onClose, 
  onSave, 
  onDelete,
  onCreateEdge,
  nodeId
}) => {
  console.log('ElementEditor initialized with element:', element);
  
  // Initialize state with element values if available
  const [label, setLabel] = useState(element?.label || '');
  const [textColor, setTextColor] = useState(element?.textColor || '#000000');
  const [fillColor, setFillColor] = useState(element?.fillColor || '#ffffff');
  const [borderColor, setBorderColor] = useState(element?.borderColor || '#000000');
  const [borderStyle, setBorderStyle] = useState(element?.borderStyle || 'solid');
  const [borderWidth, setBorderWidth] = useState(element?.borderWidth || 'normal');
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [activeTab, setActiveTab] = useState('style'); // 'style', 'text', 'code', 'info'

  // Available colors for the color picker
  const colors = [
    '#000000', '#71717A', '#A1A1AA', '#D4D4D8', '#E4E4E7', '#FFFFFF',
    '#EF4444', '#F87171', '#FB7185',
    '#C084FC', '#A78BFA', '#8B5CF6', '#06B6D4', '#22D3EE', '#67E8F9', '#38BDF8', '#60A5FA', '#3B82F6',
    '#10B981', '#34D399', '#A3E635', '#FBBF24', '#FB923C', '#F97316', '#7C3AED'
  ];

  // Border style options
  const borderOptions = [
    { type: 'thin', label: 'Thin', style: 'solid', width: '1px' },
    { type: 'normal', label: 'Normal', style: 'solid', width: '2px' },
    { type: 'thick', label: 'Thick', style: 'solid', width: '3px' },
    { type: 'thin-dashed', label: 'Thin Dashed', style: 'dashed', width: '1px' },
    { type: 'dashed', label: 'Dashed', style: 'dashed', width: '2px' },
    { type: 'thick-dashed', label: 'Thick Dashed', style: 'dashed', width: '3px' },
    { type: 'none', label: 'No border', style: 'none', width: '0px' }
  ];

  // Initialize state when element changes
  useEffect(() => {
    if (element) {
      console.log('Element received in ElementEditor:', element);
      console.log('Element label:', element.label);
      setLabel(element.label || '');
      setTextColor(element.textColor || '#000000');
      setFillColor(element.fillColor || '#ffffff');
      setBorderColor(element.borderColor || '#000000');
      setBorderStyle(element.borderStyle || 'solid');
      setBorderWidth(element.borderWidth || 'normal');
    }
  }, [element]);

  // Highlight the selected element in the graph
  useEffect(() => {
    if (visible && nodeId) {
      // Find the element in the SVG
      const svgElement = document.querySelector(`#${nodeId}`);
      if (svgElement) {
        // Add a highlight class
        svgElement.classList.add('editor-selected-element');
        
        // Remove the highlight when the editor is closed
        return () => {
          svgElement.classList.remove('editor-selected-element');
        };
      }
    }
  }, [visible, nodeId]);

  if (!visible) return null;

  console.log('Rendering ElementEditor with label:', label);
  console.log('Current element:', element);

  const handleSave = () => {
    onSave({
      label,
      textColor,
      fillColor,
      borderColor,
      borderStyle,
      borderWidth
    });
  };

  const handleColorSelect = (color) => {
    if (showColorPicker === 'text') {
      setTextColor(color);
    } else if (showColorPicker === 'fill') {
      setFillColor(color);
    } else if (showColorPicker === 'border') {
      setBorderColor(color);
    }
    setShowColorPicker(null);
  };

  const handleBorderStyleSelect = (option) => {
    setBorderStyle(option.style);
    setBorderWidth(option.type);
  };

  // Function to determine if a color is light or dark
  const isLightColor = (color) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate brightness (YIQ formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };

  // Get element type name for display
  const getElementTypeName = () => {
    if (!element) return 'Element';
    
    switch (element.nodeType) {
      case 'node':
        return 'Node';
      case 'edge':
        return 'Edge';
      case 'cluster':
        return 'Cluster';
      default:
        return 'Element';
    }
  };

  return (
    <div 
      className={`element-editor ${!visible ? 'hidden' : ''}`}
    >
      <div className="editor-header">
        <div className="editor-title">{getElementTypeName()} Properties</div>
        <div className="editor-close" onClick={onClose}>
          <FiX />
        </div>
      </div>

      <div className="editor-tabs">
        <button 
          className={`editor-tab ${activeTab === 'style' ? 'active' : ''}`}
          onClick={() => setActiveTab('style')}
        >
          <FiSettings />
          <span>Style</span>
        </button>
        <button 
          className={`editor-tab ${activeTab === 'text' ? 'active' : ''}`}
          onClick={() => setActiveTab('text')}
        >
          <FiType />
          <span>Text</span>
        </button>
        <button 
          className={`editor-tab ${activeTab === 'code' ? 'active' : ''}`}
          onClick={() => setActiveTab('code')}
        >
          <FiCode />
          <span>Code</span>
        </button>
        <button 
          className={`editor-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <FiInfo />
          <span>Info</span>
        </button>
      </div>

      <div className="editor-content">
        {activeTab === 'style' && (
          <>
            <div className="editor-toolbar">
              <button 
                className={`editor-tool-button ${showColorPicker === 'fill' ? 'active' : ''}`}
                onClick={() => setShowColorPicker(showColorPicker === 'fill' ? null : 'fill')}
                title="Edit fill color"
              >
                <div className="tool-icon fill-icon">
                  <FiSquare />
                </div>
                <span className="tool-label">Fill</span>
                <div className="color-indicator" style={{ backgroundColor: fillColor }}></div>
              </button>
              <button 
                className={`editor-tool-button ${showColorPicker === 'text' ? 'active' : ''}`}
                onClick={() => setShowColorPicker(showColorPicker === 'text' ? null : 'text')}
                title="Edit text color"
              >
                <div className="tool-icon text-icon">
                  <FiType />
                </div>
                <span className="tool-label">Text</span>
                <div className="color-indicator" style={{ backgroundColor: textColor }}></div>
              </button>
              <button 
                className={`editor-tool-button ${showColorPicker === 'border' ? 'active' : ''}`}
                onClick={() => setShowColorPicker(showColorPicker === 'border' ? null : 'border')}
                title="Edit border color"
              >
                <div className="tool-icon border-icon">
                  <FiCircle />
                </div>
                <span className="tool-label">Border</span>
                <div className="color-indicator" style={{ backgroundColor: borderColor }}></div>
              </button>
            </div>

            {/* Border style options */}
            <div className="border-options">
              <div className="border-options-label">Border Style</div>
              <div className="border-options-grid">
                {borderOptions.map((option) => (
                  <div 
                    key={option.type}
                    className={`border-option ${borderStyle === option.style && (borderWidth === option.type || (option.type === 'normal' && !borderWidth)) ? 'active' : ''}`}
                    onClick={() => handleBorderStyleSelect(option)}
                    style={{
                      borderStyle: option.style,
                      borderWidth: option.width,
                      borderColor: option.style === 'none' ? 'transparent' : borderColor
                    }}
                  >
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Color preview section */}
            <div className="color-preview-section">
              <div className="color-preview-label">Current styles:</div>
              <div 
                className="color-preview-box"
                style={{
                  backgroundColor: fillColor,
                  color: textColor,
                  borderColor: borderColor,
                  borderStyle: borderStyle,
                  borderWidth: borderWidth === 'thin' ? '1px' : borderWidth === 'thick' ? '3px' : borderWidth === 'none' ? '0' : '2px'
                }}
              >
                <span style={{ color: textColor }}>{label || 'Sample Text'}</span>
              </div>
            </div>

            {showColorPicker && (
              <div className="editor-color-picker">
                <label>
                  {showColorPicker === 'text' ? 'Edit text color' : 
                  showColorPicker === 'fill' ? 'Edit fill color' : 'Edit border color'}
                </label>
                <div className="color-grid">
                  {colors.map((color) => (
                    <div
                      key={color}
                      className={`color-option ${
                        (showColorPicker === 'text' && color === textColor) ||
                        (showColorPicker === 'fill' && color === fillColor) ||
                        (showColorPicker === 'border' && color === borderColor)
                          ? 'selected'
                          : ''
                      }`}
                      style={{ 
                        backgroundColor: color,
                        color: isLightColor(color) ? '#000000' : '#ffffff'
                      }}
                      onClick={() => handleColorSelect(color)}
                    >
                      {(showColorPicker === 'text' && color === textColor) ||
                      (showColorPicker === 'fill' && color === fillColor) ||
                      (showColorPicker === 'border' && color === borderColor) 
                        ? '✓' 
                        : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'text' && (
          <div className="text-editor-tab">
            <div className="editor-field">
              <label>Edit label</label>
              <textarea
                value={label}
                onChange={(e) => {
                  console.log('Label changed to:', e.target.value);
                  setLabel(e.target.value);
                }}
                className="editor-textarea"
                placeholder="Enter label text"
                rows={5}
              />
            </div>
            
            <div className="text-preview">
              <div className="text-preview-label">Preview:</div>
              <div className="text-preview-content" style={{ color: textColor, backgroundColor: fillColor }}>
                {label || 'No text entered'}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'code' && (
          <div className="code-editor-tab">
            <div className="code-info">
              <p>Element ID: <code>{nodeId}</code></p>
              <p>Type: <code>{element?.nodeType || 'Unknown'}</code></p>
              <p>Mermaid code for this element:</p>
              <pre className="code-preview">
                {element?.nodeType === 'node' 
                  ? `${nodeId?.replace(/^flowchart-/, '')}[${label}]`
                  : element?.nodeType === 'edge'
                    ? `Source --> Target|${label}|`
                    : 'Code not available'}
              </pre>
              <p>Style properties:</p>
              <pre className="code-preview">
                {`style ${nodeId?.replace(/^flowchart-/, '')} fill:${fillColor},color:${textColor},stroke:${borderColor}${borderStyle === 'dashed' ? ',stroke-dasharray:5 5' : ''}${borderWidth === 'thick' ? ',stroke-width:3px' : borderWidth === 'thin' ? ',stroke-width:1px' : ''}`}
              </pre>
            </div>
          </div>
        )}

        {activeTab === 'info' && (
          <div className="info-editor-tab">
            <h3>Element Information</h3>
            <div className="info-item">
              <span className="info-label">Element Type:</span>
              <span className="info-value">{getElementTypeName()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Element ID:</span>
              <span className="info-value">{nodeId}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Label:</span>
              <span className="info-value">{label || '(empty)'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fill Color:</span>
              <span className="info-value">
                <span className="color-swatch" style={{ backgroundColor: fillColor }}></span>
                {fillColor}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Text Color:</span>
              <span className="info-value">
                <span className="color-swatch" style={{ backgroundColor: textColor }}></span>
                {textColor}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Border Color:</span>
              <span className="info-value">
                <span className="color-swatch" style={{ backgroundColor: borderColor }}></span>
                {borderColor}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Border Style:</span>
              <span className="info-value">{borderStyle}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Border Width:</span>
              <span className="info-value">{borderWidth}</span>
            </div>
          </div>
        )}

        <div className="editor-actions">
          <button 
            className="editor-button delete-button" 
            onClick={onDelete}
          >
            <FiTrash2 /> Delete
          </button>
          {element?.nodeType === 'node' && (
            <button 
              className="editor-button edge-button" 
              onClick={onCreateEdge}
            >
              <FiArrowRight /> Create Edge
            </button>
          )}
          <button 
            className="editor-button save-button" 
            onClick={handleSave}
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ElementEditor; 